// Lo que la pantalla de generación dice de sí misma en dos lugares: el chip de
// estado del encabezado y el aviso "Desde su última sesión". Es lógica pura y
// se prueba sin montar nada. NADA se decide acá que el servidor ya decida: el
// estado del encabezado se DERIVA de lo que ya se sabe (la corrida del
// análisis, las condiciones de emisión y las emisiones).

import { estadoDeLaVistaPrevia, type CondicionesDeEmision, type Emision } from "./emision";
import { conteoDeDudas, filtrarDudas, type CorridaDelAnalisis, type Duda, type FiltroDeDudas } from "./generacion";
import type { DatosDeSecciones } from "./redaccion";
import { rubrosPorFijar, type Valorizacion } from "./valorizacion";

export type TonoDelEncabezado = "reposo" | "info" | "aviso" | "ok";

/**
 * El chip del encabezado. El informe tiene su propio estado ("En proceso"), pero
 * eso no dice en qué punto de ESTA pantalla se está: se deriva del análisis y de
 * la emisión. Tres estados reales —analizando, en revisión, emitido— más "sin
 * analizar" cuando todavía no hubo corrida.
 */
export function estadoDelEncabezado(
  corrida: CorridaDelAnalisis | null | undefined,
  condiciones: CondicionesDeEmision | undefined,
  emisiones: Emision[],
): { texto: string; tono: TonoDelEncabezado } {
  if (corrida?.estado === "en_curso") return { texto: "ANALIZANDO", tono: "info" };
  if (corrida === null || corrida === undefined) return { texto: "SIN ANALIZAR", tono: "reposo" };

  if (emisiones.length > 0 && estadoDeLaVistaPrevia(condiciones, emisiones).tono === "ok") {
    return { texto: "EMITIDO", tono: "ok" };
  }

  return { texto: "EN REVISIÓN", tono: "aviso" };
}

const plural = (n: number, singular: string, forma: string) => `${n} ${n === 1 ? singular : forma}`;

export type DestinoDelAviso =
  | { fase: 1; filtro: FiltroDeDudas; dudaId: number; texto: string }
  | { fase: 2; texto: string }
  | { fase: 3; texto: string };

/**
 * Lo que sigue pendiente en TODA la pantalla (dudas, rubros por fijar y secciones
 * por validar), dicho en frases cortas, y a qué fase lleva el botón del aviso: la
 * primera que tenga algo, en el orden del proceso. Lo que bloquea va primero.
 */
export function pendientesDeLaPantalla(
  dudas: Duda[],
  valorizacion: Valorizacion | undefined,
  secciones: DatosDeSecciones | undefined,
): { hay: boolean; frases: string[]; destino: DestinoDelAviso | null } {
  const c = conteoDeDudas(dudas);
  const advertencias = c.abiertas - c.bloquean;
  const rubros = valorizacion === undefined ? 0 : rubrosPorFijar(valorizacion).length;
  const resumen = secciones?.resumen;
  const porValidar = resumen === undefined ? 0 : Math.max(0, resumen.redactables - resumen.validadas);
  const desactualizadas = resumen?.desactualizadas ?? 0;

  const frases: string[] = [];
  if (c.bloquean > 0) frases.push(c.bloquean === 1 ? "1 duda bloquea" : `${c.bloquean} dudas bloquean`);
  if (advertencias > 0) frases.push(plural(advertencias, "advertencia abierta", "advertencias abiertas"));
  if (rubros > 0) frases.push(plural(rubros, "rubro por fijar", "rubros por fijar"));
  if (porValidar > 0) {
    const sufijo = desactualizadas > 0 ? ` (${plural(desactualizadas, "desactualizada", "desactualizadas")})` : "";
    frases.push(`${plural(porValidar, "sección por validar", "secciones por validar")}${sufijo}`);
  }

  let destino: DestinoDelAviso | null = null;
  const bloqueante = filtrarDudas(dudas, "bloquean")[0];
  const abierta = filtrarDudas(dudas, "abiertas")[0];

  if (bloqueante) destino = { fase: 1, filtro: "bloquean", dudaId: bloqueante.id, texto: "Responder dudas" };
  else if (rubros > 0) destino = { fase: 2, texto: "Fijar valores" };
  else if (porValidar > 0) destino = { fase: 3, texto: "Revisar secciones" };
  else if (abierta) destino = { fase: 1, filtro: "abiertas", dudaId: abierta.id, texto: "Revisar dudas" };

  return { hay: frases.length > 0, frases, destino };
}

/** El aviso rojo de las fases 2 y 3: lo que bloquea sigue vivo aunque se trabaje en otra fase. */
export function avisoDeDudasQueBloquean(cantidad: number): string {
  const cuantas = cantidad === 1 ? "1 duda bloquea" : `${cantidad} dudas bloquean`;
  const final = cantidad === 1 ? "responderla" : "responderlas";

  return `${cuantas} el avance. Puedes seguir trabajando aquí, pero no se podrá emitir hasta ${final}.`;
}
